import React, { useState, useEffect } from 'react';
import { CreditCard, Plus, Trash2, CheckCircle, Loader2 } from 'lucide-react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';

interface PaymentMethod {
  id: string;
  type: 'card';
  brand: string;
  last4: string;
  expiryMonth: number;
  expiryYear: number;
  isDefault: boolean;
}

// Initialize Stripe
let stripePromise: Promise<any> | null = null;
const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const getStripe = async () => {
  if (!stripePromise) {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Authentication required. Please log in again.');
      }

      const response = await fetch(`${apiUrl}/api/stripe/config`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch Stripe config: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      
      if (!data.success) {
        throw new Error(data.message || 'Failed to load Stripe configuration');
      }

      if (!data.data?.publishableKey) {
        throw new Error('Stripe publishable key is missing. Please check your server configuration.');
      }

      stripePromise = loadStripe(data.data.publishableKey);
    } catch (error) {
      console.error('Failed to load Stripe config:', error);
      throw error; // Re-throw to allow caller to handle
    }
  }
  return stripePromise;
};

// Add Card Form Component
const AddCardForm: React.FC<{ onSuccess: () => void; onCancel: () => void }> = ({ onSuccess, onCancel }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!stripe || !elements) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('token');
      
      // Create setup intent
      const setupResponse = await fetch(`${apiUrl}/api/stripe/setup-intent`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      const setupData = await setupResponse.json();
      
      if (!setupData.success) {
        throw new Error(setupData.message || 'Failed to create setup intent');
      }

      // Confirm card setup
      const cardElement = elements.getElement(CardElement);
      if (!cardElement) {
        throw new Error('Card element not found');
      }

      const { setupIntent, error: stripeError } = await stripe.confirmCardSetup(
        setupData.data.clientSecret,
        {
          payment_method: {
            card: cardElement,
          }
        }
      );

      if (stripeError) {
        throw new Error(stripeError.message);
      }

      if (setupIntent?.status === 'succeeded') {
        onSuccess();
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add card');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Card Information
        </label>
        <div className="p-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700">
          <CardElement
            options={{
              style: {
                base: {
                  fontSize: '16px',
                  color: '#424770',
                  '::placeholder': {
                    color: '#aab7c4',
                  },
                },
                invalid: {
                  color: '#9e2146',
                },
              },
            }}
          />
        </div>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 dark:bg-gray-700 dark:border dark:border-error-800/50 border border-red-200 dark:border-red-800 rounded-lg">
          <p className="text-sm text-error-800 dark:text-error-300">{error}</p>
        </div>
      )}

      <div className="flex space-x-3">
        <button
          type="button"
          onClick={onCancel}
          disabled={loading}
          className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={!stripe || loading}
          className="flex-1 px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg disabled:opacity-50 flex items-center justify-center"
        >
          {loading ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Adding...
            </>
          ) : (
            'Add Card'
          )}
        </button>
      </div>
    </form>
  );
};

export const AccountPaymentMethods: React.FC = () => {
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [showAddCard, setShowAddCard] = useState(false);
  const [loading, setLoading] = useState(true);
  const [stripeLoaded, setStripeLoaded] = useState(false);
  const [stripeError, setStripeError] = useState<string | null>(null);

  useEffect(() => {
    loadPaymentMethods();
    initializeStripe();
  }, []);

  const initializeStripe = async () => {
    try {
      await getStripe();
      setStripeLoaded(true);
      setStripeError(null);
    } catch (error) {
      console.error('Stripe initialization error:', error);
      setStripeError(error instanceof Error ? error.message : 'Failed to initialize payment system');
      setStripeLoaded(false);
    }
  };

  const loadPaymentMethods = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Authentication required. Please log in again.');
      }

      const response = await fetch(`${apiUrl}/api/stripe/payment-methods`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: `Server error: ${response.status}` }));
        throw new Error(errorData.message || `Failed to load payment methods: ${response.status}`);
      }
      
      const data = await response.json();
      if (data.success) {
        setPaymentMethods(data.data || []);
      } else {
        throw new Error(data.message || 'Failed to load payment methods');
      }
    } catch (error) {
      console.error('Failed to load payment methods:', error);
      // Don't set error state here - let the component handle it gracefully
      // Payment methods will just be empty array
    } finally {
      setLoading(false);
    }
  };

  const handleSetDefault = async (id: string) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${apiUrl}/api/stripe/payment-methods/${id}/set-default`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      const data = await response.json();
      if (data.success) {
        await loadPaymentMethods();
      }
    } catch (error) {
      console.error('Failed to set default payment method:', error);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to remove this payment method?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${apiUrl}/api/stripe/payment-methods/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      const data = await response.json();
      if (data.success) {
        await loadPaymentMethods();
      }
    } catch (error) {
      console.error('Failed to delete payment method:', error);
    }
  };

  const getCardIcon = (brand: string) => {
    return <CreditCard className="w-8 h-8" />;
  };

  const handleCardAdded = () => {
    setShowAddCard(false);
    loadPaymentMethods();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <Loader2 className="w-8 h-8 text-primary-500 animate-spin" />
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">Payment Methods</h3>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Manage your payment methods for subscriptions and purchases
          </p>
        </div>
        <button
          onClick={() => {
            if (stripeError) {
              initializeStripe();
            }
            setShowAddCard(true);
          }}
          className="flex items-center space-x-2 px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          disabled={loading}
        >
          <Plus className="w-4 h-4" />
          <span>Add Card</span>
        </button>
      </div>

      {/* Payment Methods List */}
      {paymentMethods.length === 0 ? (
        <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-12 border border-gray-200 dark:border-gray-600">
          <div className="flex flex-col items-center justify-center text-center">
            <CreditCard className="w-12 h-12 text-gray-400 mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
              No Payment Methods
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              Add a payment method to subscribe to premium features
            </p>
            <button
              onClick={() => {
                if (stripeError) {
                  initializeStripe();
                }
                setShowAddCard(true);
              }}
              className="flex items-center space-x-2 px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={loading}
            >
              <Plus className="w-4 h-4" />
              <span>Add Your First Card</span>
            </button>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          {paymentMethods.map((method) => (
            <div
              key={method.id}
              className="bg-white dark:bg-gray-700 rounded-lg p-6 border border-gray-200 dark:border-gray-600"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div className="text-gray-600 dark:text-gray-400">
                    {getCardIcon(method.brand)}
                  </div>
                  <div>
                    <div className="flex items-center space-x-2">
                      <h4 className="font-semibold text-gray-900 dark:text-gray-100 capitalize">
                        {method.brand} •••• {method.last4}
                      </h4>
                      {method.isDefault && (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold bg-success-100 text-success-800 dark:bg-success-900 dark:text-success-300">
                          <CheckCircle className="w-3 h-3 mr-1" />
                          Default
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Expires {method.expiryMonth.toString().padStart(2, '0')}/{method.expiryYear}
                    </p>
                  </div>
                </div>

                <div className="flex items-center space-x-2">
                  {!method.isDefault && (
                    <button
                      onClick={() => handleSetDefault(method.id)}
                      className="px-3 py-2 text-sm text-primary-600 dark:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/20 rounded-lg transition-colors"
                    >
                      Set as Default
                    </button>
                  )}
                  <button
                    onClick={() => handleDelete(method.id)}
                    className="p-2 text-error-600 dark:text-error-400 hover:bg-error-50 dark:hover:bg-error-900/20 rounded-lg transition-colors"
                    disabled={method.isDefault && paymentMethods.length === 1}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Stripe Error Banner */}
      {stripeError && !showAddCard && (
        <div className="mt-6 p-4 bg-red-50 dark:bg-gray-700 dark:border dark:border-error-800/50 rounded-lg border border-red-200 dark:border-red-800">
          <div className="flex items-start space-x-3">
            <CreditCard className="w-5 h-5 text-error-600 dark:text-error-400 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm text-error-800 dark:text-error-300 font-medium mb-1">
                Payment System Error
              </p>
              <p className="text-xs text-error-700 dark:text-error-400 mb-2">
                {stripeError}
              </p>
              <button
                onClick={initializeStripe}
                className="text-xs text-error-600 dark:text-error-400 hover:underline"
              >
                Retry initialization
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Stripe Integration Info */}
      {!stripeError && (
        <div className="mt-6 p-4 bg-accent-50 dark:bg-gray-700 dark:border dark:border-accent-800/50 rounded-lg border border-accent-200 dark:border-accent-800">
          <div className="flex items-start space-x-3">
            <CreditCard className="w-5 h-5 text-accent-600 dark:text-accent-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm text-accent-800 dark:text-accent-300 font-medium mb-1">
                Secure Payment Processing
              </p>
              <p className="text-xs text-accent-700 dark:text-accent-400">
                All payments are securely processed through Stripe. Your card information is encrypted and never stored on our servers.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Add Card Modal */}
      {showAddCard && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md mx-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                Add Payment Method
              </h3>
              <button
                onClick={() => {
                  setShowAddCard(false);
                  setStripeError(null);
                }}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                ✕
              </button>
            </div>
            {stripeError ? (
              <div className="p-4 bg-red-50 dark:bg-gray-700 dark:border dark:border-error-800/50 border border-red-200 dark:border-red-800 rounded-lg">
                <p className="text-sm text-error-800 dark:text-error-300 mb-4">{stripeError}</p>
                <button
                  onClick={initializeStripe}
                  className="px-4 py-2 bg-error-600 hover:bg-error-700 text-white rounded-lg text-sm"
                >
                  Retry
                </button>
              </div>
            ) : stripeLoaded && stripePromise ? (
              <Elements stripe={stripePromise}>
                <AddCardForm 
                  onSuccess={handleCardAdded}
                  onCancel={() => {
                    setShowAddCard(false);
                    setStripeError(null);
                  }}
                />
              </Elements>
            ) : (
              <div className="flex items-center justify-center p-8">
                <Loader2 className="w-6 h-6 text-primary-500 animate-spin" />
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
